import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  IFilter,
  IFilterManager,
  FilterState,
  ActiveFilter,
  FilterChangeCallback
} from './filter.interfaces';

@Injectable({
  providedIn: 'root'
})
export class FilterManagerService implements IFilterManager {
  private filters = new Map<string, IFilter>();
  private pageCallbacks: FilterChangeCallback[] = [];
  private filtersChangedSubject = new Subject<void>();

  registerFilter(filter: IFilter): void {
    this.filters.set(filter.filterId, filter);
    this.filtersChangedSubject.next();
  }

  unregisterFilter(filterId: string): void {
    this.filters.delete(filterId);
  }

  registerPageCallback(callback: FilterChangeCallback): void {
    this.pageCallbacks.push(callback);
  }

  removePageCallback(callback: FilterChangeCallback): void {
    this.pageCallbacks = this.pageCallbacks.filter(cb => cb !== callback);
  }

  notifyFilterChange(filterId: string, value: any): void {
    this.filtersChangedSubject.next();
    this.notifyPageCallbacks();
  }

  getFiltersChanged$() {
    return this.filtersChangedSubject.asObservable();
  }

  getFilterState(): FilterState {
    const state: FilterState = {};
    this.filters.forEach((filter, id) => {
      const value = filter.getValue();
      if (value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        state[id] = value;
      }
    });
    return state;
  }

  getActiveFilters(): ActiveFilter[] {
    const activeFilters: ActiveFilter[] = [];
    this.filters.forEach((filter) => {
      if (filter.hasValue()) {
        activeFilters.push({
          filterId: filter.filterId,
          label: filter.getFilterLabel(),
          displayValue: filter.getDisplayValue(),
          clearable: true
        });
      }
    });
    return activeFilters;
  }

  resetFilter(filterId: string): void {
    const filter = this.filters.get(filterId);
    if (filter) {
      filter.reset();
      this.filtersChangedSubject.next();
      this.notifyPageCallbacks();
    }
  }

  resetAllFilters(): void {
    this.filters.forEach(filter => filter.reset());
    this.filtersChangedSubject.next();
    this.notifyPageCallbacks();
  }

  isFilterActive(filterId: string): boolean {
    const filter = this.filters.get(filterId);
    return filter ? filter.hasValue() : false;
  }

  private notifyPageCallbacks(): void {
    const state = this.getFilterState();
    this.pageCallbacks.forEach(cb => cb(state));
  }
}
